import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { X, Plus, Trash2, Layers, Building2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/hooks/use-toast';
import { getMetaPages, getPageCategories } from '@/services/api/connections';
import {
    createIceBreaker, updateIceBreaker, listFlowTriggers,
    type IceBreakerRow, type IceBreakerQuestion, type IceBreakerScope,
    type FlowTrigger,
} from '@/services/api/ice-breakers';

interface Props {
    initial: IceBreakerRow | null;
    onClose: () => void;
    onSaved: () => void;
}

const emptyQuestion = (): IceBreakerQuestion => ({
    question: '',
    payload: '',
    action_type: 'postback',
    flow_id: null,
});

export default function IceBreakerForm({ initial, onClose, onSaved }: Props) {
    const { toast } = useToast();
    const isEdit = !!initial;

    const [scope, setScope] = useState<IceBreakerScope>(initial?.scope || 'category');
    const [pageId, setPageId] = useState<string>(initial?.page_id || '');
    const [categoryId, setCategoryId] = useState<number | null>(initial?.category_id || null);
    const [questions, setQuestions] = useState<IceBreakerQuestion[]>(
        initial?.questions?.length ? initial.questions : [emptyQuestion()]
    );
    const [isActive, setIsActive] = useState<boolean>(initial?.is_active ?? true);

    // Sources
    const { data: pages = [] } = useQuery({
        queryKey: ['meta-pages'],
        queryFn: getMetaPages,
        staleTime: 1000 * 60,
    });
    const { data: categories = [] } = useQuery({
        queryKey: ['page-categories'],
        queryFn: getPageCategories,
        staleTime: 1000 * 60,
    });
    const { data: triggers = [] } = useQuery<FlowTrigger[]>({
        queryKey: ['flow-triggers'],
        queryFn: listFlowTriggers,
        staleTime: 1000 * 60,
    });

    // Saving mutations
    const createMutation = useMutation({
        mutationFn: createIceBreaker,
        onSuccess: () => {
            toast({ title: 'Configurado', description: 'Ice breaker criado e sincronizado com Meta.' });
            onSaved();
        },
        onError: (e: any) => {
            toast({
                title: 'Erro ao criar',
                description: e?.response?.data?.detail || 'Falha ao criar',
                variant: 'destructive',
            });
        },
    });

    const updateMutation = useMutation({
        mutationFn: (payload: any) => updateIceBreaker(initial!.id, payload),
        onSuccess: () => {
            toast({ title: 'Atualizado', description: 'Ice breaker re-sincronizado com Meta.' });
            onSaved();
        },
        onError: (e: any) => {
            toast({
                title: 'Erro ao atualizar',
                description: e?.response?.data?.detail || 'Falha ao atualizar',
                variant: 'destructive',
            });
        },
    });

    const updateQuestion = (idx: number, patch: Partial<IceBreakerQuestion>) => {
        setQuestions(prev => prev.map((q, i) => {
            if (i !== idx) return q;
            const merged = { ...q, ...patch };

            // Regra: payload sempre acompanha a question.
            //  - postback: user só escreve a pergunta → payload = pergunta
            //  - flow: user escolhe um trigger → pergunta + payload = trigger_value
            if (patch.action_type) {
                // Resetou o tipo: limpa campos relacionados
                merged.flow_id = null;
            }
            // Sincroniza payload com question (regra simples e previsível)
            merged.payload = merged.question;
            return merged;
        }));
    };

    /**
     * Seleção em 2 passos pra modo flow:
     *  1) pickFlow(funnel_id): zera o trigger; só guarda flow_id (pergunta vazia)
     *  2) pickTrigger(trigger): popula pergunta + payload = trigger_value
     */
    const pickFlow = (idx: number, funnel_id: number | null) => {
        setQuestions(prev => prev.map((q, i) => i === idx ? {
            ...q,
            action_type: 'flow',
            flow_id: funnel_id,
            question: '',
            payload: '',
        } : q));
    };

    const pickTrigger = (idx: number, trigger: FlowTrigger) => {
        setQuestions(prev => prev.map((q, i) => i === idx ? {
            ...q,
            action_type: 'flow',
            flow_id: trigger.funnel_id,
            question: trigger.trigger_value.slice(0, 80),
            payload: trigger.trigger_value.slice(0, 80),
        } : q));
    };

    // Agrupa triggers por funnel (memo simples — recomputa só quando triggers muda)
    const flowsWithTriggers = (() => {
        const map = new Map<number, { funnel_id: number; funnel_name: string; triggers: FlowTrigger[] }>();
        for (const t of triggers) {
            if (!map.has(t.funnel_id)) {
                map.set(t.funnel_id, { funnel_id: t.funnel_id, funnel_name: t.funnel_name, triggers: [] });
            }
            map.get(t.funnel_id)!.triggers.push(t);
        }
        return Array.from(map.values()).sort((a, b) => a.funnel_name.localeCompare(b.funnel_name));
    })();

    const addQuestion = () => {
        if (questions.length >= 4) return;
        setQuestions(prev => [...prev, emptyQuestion()]);
    };

    const removeQuestion = (idx: number) => {
        if (questions.length <= 1) return;
        setQuestions(prev => prev.filter((_, i) => i !== idx));
    };

    const validate = (): string | null => {
        if (scope === 'page' && !pageId) return 'Selecione a página';
        if (scope === 'category' && !categoryId) return 'Selecione a categoria';
        if (!questions.length) return 'Adicione pelo menos 1 pergunta';
        for (let i = 0; i < questions.length; i++) {
            const q = questions[i];
            if (!q.question.trim()) return `Pergunta ${i + 1}: texto vazio`;
            if (q.question.length > 80) return `Pergunta ${i + 1}: máximo 80 caracteres`;
            if (q.action_type === 'flow' && !q.flow_id) {
                return `Pergunta ${i + 1}: escolha um trigger de funil`;
            }
            // payload sempre é setado automaticamente pelo updateQuestion,
            // mas vamos garantir
            if (!q.payload?.trim()) return `Pergunta ${i + 1}: payload vazio`;
        }
        return null;
    };

    const handleSubmit = () => {
        const err = validate();
        if (err) {
            toast({ title: 'Erro de validação', description: err, variant: 'destructive' });
            return;
        }

        if (isEdit) {
            updateMutation.mutate({ questions, is_active: isActive });
        } else {
            createMutation.mutate({
                scope,
                page_id: scope === 'page' ? pageId : undefined,
                category_id: scope === 'category' ? categoryId! : undefined,
                questions,
                is_active: isActive,
            });
        }
    };

    const isSaving = createMutation.isPending || updateMutation.isPending;

    return (
        <div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] px-6 py-5 text-white flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-bold">
                            {isEdit ? 'Editar quebra-gelo' : 'Nova configuração'}
                        </h2>
                        <p className="text-xs text-white/60 mt-0.5">
                            Mensagens recomendadas na primeira interação
                        </p>
                    </div>
                    <button onClick={onClose} className="text-white/60 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {/* Scope (só mostra se for criar) */}
                    {!isEdit && (
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700">Aplicar em</Label>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => setScope('category')}
                                    className={`p-3 rounded-lg border-2 transition-colors text-left ${scope === 'category'
                                        ? 'border-violet-500 bg-violet-50'
                                        : 'border-gray-200 bg-white hover:border-gray-300'}`}
                                >
                                    <Layers className={`w-5 h-5 mb-1.5 ${scope === 'category' ? 'text-violet-600' : 'text-gray-400'}`} />
                                    <p className="text-sm font-semibold">Categoria</p>
                                    <p className="text-[11px] text-gray-500 mt-0.5">
                                        Aplica em todas as páginas da categoria (e futuras)
                                    </p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setScope('page')}
                                    className={`p-3 rounded-lg border-2 transition-colors text-left ${scope === 'page'
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 bg-white hover:border-gray-300'}`}
                                >
                                    <Building2 className={`w-5 h-5 mb-1.5 ${scope === 'page' ? 'text-blue-600' : 'text-gray-400'}`} />
                                    <p className="text-sm font-semibold">Página específica</p>
                                    <p className="text-[11px] text-gray-500 mt-0.5">
                                        Sobrepõe a config da categoria
                                    </p>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Target selector */}
                    {!isEdit && scope === 'category' && (
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700">Categoria</Label>
                            {categories.length === 0 ? (
                                <div className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-500">
                                    Nenhuma categoria. Crie em "Conexões → Categorias".
                                </div>
                            ) : (
                                <SearchableSelect
                                    value={categoryId ? String(categoryId) : ''}
                                    onValueChange={v => setCategoryId(parseInt(v))}
                                    options={categories.map(c => ({
                                        value: String(c.id),
                                        label: `${c.name} (${c.pages_count} ${c.pages_count === 1 ? 'página' : 'páginas'})`,
                                    }))}
                                    placeholder="Selecione uma categoria..."
                                    searchPlaceholder="Buscar categoria..."
                                />
                            )}
                        </div>
                    )}

                    {!isEdit && scope === 'page' && (
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700">Página</Label>
                            <SearchableSelect
                                value={pageId}
                                onValueChange={setPageId}
                                options={pages
                                    .filter(p => p.is_active)
                                    .map(p => ({ value: p.page_id, label: p.page_name }))
                                }
                                placeholder="Selecione uma página..."
                                searchPlaceholder="Buscar por nome..."
                            />
                        </div>
                    )}

                    {/* Edit mode — show readonly target */}
                    {isEdit && (
                        <div className="bg-gray-50 rounded-lg p-3 flex items-center gap-2">
                            {initial!.scope === 'category' ? (
                                <Layers className="w-4 h-4 text-violet-600" />
                            ) : (
                                <Building2 className="w-4 h-4 text-blue-600" />
                            )}
                            <span className="text-sm">
                                {initial!.scope === 'category'
                                    ? <>Categoria: <strong>{initial!.category_name}</strong></>
                                    : <>Página: <strong>{initial!.page_name}</strong></>
                                }
                            </span>
                        </div>
                    )}

                    {/* Questions */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium text-gray-700">
                                Perguntas ({questions.length}/4)
                            </Label>
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={addQuestion}
                                disabled={questions.length >= 4}
                            >
                                <Plus className="w-3.5 h-3.5 mr-1.5" />
                                Adicionar
                            </Button>
                        </div>

                        {questions.map((q, i) => {
                            // Match por (funnel_id + trigger_value) — evita ambiguidade
                            // quando 2 flows tem trigger com mesmo texto.
                            const selectedTrigger = q.action_type === 'flow'
                                ? triggers.find(t => t.funnel_id === q.flow_id && t.trigger_value === q.payload)
                                : undefined;
                            return (
                                <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2.5 bg-gray-50/30">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-semibold text-gray-500">
                                            Pergunta {i + 1}
                                        </span>
                                        {questions.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeQuestion(i)}
                                                className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>

                                    <div>
                                        <Label className="text-[11px] text-gray-500 uppercase tracking-wider">
                                            Ação ao clicar
                                        </Label>
                                        <Select
                                            value={q.action_type}
                                            onValueChange={(v: 'flow' | 'postback') => updateQuestion(i, {
                                                action_type: v,
                                                flow_id: null,
                                                question: '',
                                                payload: '',
                                            })}
                                        >
                                            <SelectTrigger className="h-9">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="flow">🔀 Disparar flow (escolher trigger)</SelectItem>
                                                <SelectItem value="postback">📝 Mensagem livre (texto custom)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {q.action_type === 'flow' ? (
                                        // ── Modo flow: 2 passos
                                        //   1) Escolhe o flow (funil)
                                        //   2) Escolhe entre os triggers DESSE flow
                                        // Selecionar trigger preenche pergunta + payload automaticamente.
                                        <div className="space-y-2">
                                            {/* Passo 1: escolha do flow */}
                                            <div className="space-y-1.5">
                                                <Label className="text-[11px] text-gray-500 uppercase tracking-wider">
                                                    1. Flow (funil)
                                                </Label>
                                                {flowsWithTriggers.length === 0 ? (
                                                    <div className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-500">
                                                        Nenhum flow ativo com triggers. Cadastre triggers nos seus flows.
                                                    </div>
                                                ) : (
                                                    <SearchableSelect
                                                        value={q.flow_id ? String(q.flow_id) : ''}
                                                        onValueChange={(v) => pickFlow(i, parseInt(v))}
                                                        options={flowsWithTriggers.map(f => ({
                                                            value: String(f.funnel_id),
                                                            label: `${f.funnel_name} (${f.triggers.length} ${f.triggers.length === 1 ? 'trigger' : 'triggers'})`,
                                                        }))}
                                                        placeholder="Selecione um flow..."
                                                        searchPlaceholder="Buscar flow..."
                                                    />
                                                )}
                                            </div>

                                            {/* Passo 2: escolha do trigger (filtrado pelo flow) */}
                                            {q.flow_id && (() => {
                                                const flowEntry = flowsWithTriggers.find(f => f.funnel_id === q.flow_id);
                                                const flowTriggers = flowEntry?.triggers ?? [];
                                                return (
                                                    <div className="space-y-1.5">
                                                        <Label className="text-[11px] text-gray-500 uppercase tracking-wider">
                                                            2. Pergunta (trigger desse flow)
                                                        </Label>
                                                        <Select
                                                            value={selectedTrigger ? String(selectedTrigger.trigger_id) : ''}
                                                            onValueChange={(v) => {
                                                                const t = flowTriggers.find(tr => String(tr.trigger_id) === v);
                                                                if (t) pickTrigger(i, t);
                                                            }}
                                                        >
                                                            <SelectTrigger className="h-9">
                                                                <SelectValue placeholder={`Escolha uma das ${flowTriggers.length} perguntas...`} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {flowTriggers.length === 0 && (
                                                                    <div className="px-3 py-2 text-xs text-gray-500">
                                                                        Esse flow não tem triggers cadastrados.
                                                                    </div>
                                                                )}
                                                                {flowTriggers.map(t => (
                                                                    <SelectItem key={t.trigger_id} value={String(t.trigger_id)}>
                                                                        {t.trigger_value}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                );
                                            })()}

                                            {q.question && (
                                                <p className="text-[11px] text-gray-500">
                                                    Vai aparecer no Messenger como: <strong className="text-gray-700">"{q.question}"</strong>
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        // ── Modo postback custom: input único; payload = pergunta.
                                        <div className="space-y-1.5">
                                            <Label className="text-[11px] text-gray-500 uppercase tracking-wider">
                                                Texto da pergunta ({q.question.length}/80)
                                            </Label>
                                            <Input
                                                value={q.question}
                                                onChange={(e) => updateQuestion(i, { question: e.target.value.slice(0, 80) })}
                                                placeholder="Ex.: Quero negociar minha dívida"
                                                maxLength={80}
                                            />
                                            <p className="text-[11px] text-gray-500">
                                                O postback enviado ao clicar será o próprio texto: <code className="font-mono bg-gray-100 px-1 rounded text-[10px]">{q.question || '(vazio)'}</code>
                                            </p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Active toggle (só em edit) */}
                    {isEdit && (
                        <label className="flex items-center gap-2 cursor-pointer p-2 rounded bg-gray-50 hover:bg-gray-100 transition-colors">
                            <input
                                type="checkbox"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                className="rounded"
                            />
                            <span className="text-sm text-gray-700">
                                Ativo (mostra no Messenger). Desativar mantém a config mas remove do Meta.
                            </span>
                        </label>
                    )}

                    {/* Hint */}
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-blue-800">
                            Ao salvar, as perguntas serão sincronizadas com a Meta automaticamente para as páginas afetadas.
                            Pode levar alguns segundos pra aparecer no Messenger.
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-gray-100 px-6 py-4 flex items-center justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={isSaving}>
                        Cancelar
                    </Button>
                    <Button onClick={handleSubmit} disabled={isSaving} className="bg-blue-600 hover:bg-blue-700 text-white">
                        {isSaving ? 'Salvando...' : isEdit ? 'Salvar e re-sincronizar' : 'Criar e sincronizar'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
